import { RefObject, useLayoutEffect } from 'react';

// Scene entrance grammar: marks [data-rv] nodes with data-shown once they enter the viewport.
// Hidden states only apply while the root carries .scene--rv, so without JS (or with reduced
// motion) everything renders visible.
export function useReveal(rootRef: RefObject<HTMLElement>) {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) return;

    root.classList.add('scene--rv');
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.setAttribute('data-shown', '');
          io.unobserve(e.target);
        }
      }),
      // threshold 0: very tall targets (the mobile pricing tower) can never reach a ratio threshold.
      { rootMargin: '0px 0px -8% 0px', threshold: 0 },
    );
    const observe = (scope: ParentNode) =>
      scope.querySelectorAll('[data-rv]:not([data-shown])').forEach((el) => io.observe(el));
    observe(root);

    // Plans and other async content mount after first paint.
    const mo = new MutationObserver(() => observe(root));
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
      root.classList.remove('scene--rv');
    };
  }, [rootRef]);
}
